import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CarouselSection } from "../../content/industry";
import { Photo } from "./Photo";
import { SectionHeading } from "./SectionHeading";

/**
 * Carrusel de imágenes accesible. Las diapositivas son una tira con desplazamiento por pasos (scroll-snap), así que se
 * puede deslizar con el dedo o el trackpad aunque JavaScript tarde en cargar; las flechas, los puntos y las teclas
 * izquierda y derecha solo mueven esa tira. El avance automático es opcional y nunca corre si el visitante pidió reducir el movimiento.
 */
export function Carousel({ section }: { section: CarouselSection }) {
  const track = useRef<HTMLUListElement>(null);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = section.items.length;

  const goTo = useCallback((index: number) => {
    const list = track.current;
    if (!list || count === 0) return;
    const target = (index + count) % count; // al pasar del final vuelve al principio
    setCurrent(target);
    list.scrollTo({ left: target * list.clientWidth, behavior: "smooth" });
  }, [count]);

  // Al deslizar con el dedo o el trackpad, la diapositiva actual se deduce de la posición de la tira cuando se detiene.
  useEffect(() => {
    const list = track.current;
    if (!list) return;
    let timer = 0;
    const update = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setCurrent(Math.round(list.scrollLeft / Math.max(1, list.clientWidth))), 120);
    };
    list.addEventListener("scroll", update, { passive: true });
    return () => { window.clearTimeout(timer); list.removeEventListener("scroll", update); };
  }, []);

  useEffect(() => {
    if (!section.autoplay || paused || count < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => goTo(current + 1), Math.max(3000, section.interval ?? 6000));
    return () => window.clearInterval(timer);
  }, [section.autoplay, section.interval, paused, count, current, goTo]);

  if (count === 0) return null;

  return (
    <section id={section.id} className="ix-section ix-carousel" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} text={section.text} />
        <div
          className="ix-carousel-frame"
          role="region"
          aria-roledescription="carrusel"
          aria-label={section.title}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onKeyDown={event => {
            if (event.key === "ArrowLeft") { event.preventDefault(); goTo(current - 1); }
            if (event.key === "ArrowRight") { event.preventDefault(); goTo(current + 1); }
          }}
        >
          <ul ref={track} className="ix-carousel-track" tabIndex={0} aria-live={section.autoplay && !paused ? "off" : "polite"}>
            {section.items.map((item, index) => <li
              key={`${item.image}-${item.caption}`}
              className="ix-carousel-slide"
              role="group"
              aria-roledescription="diapositiva"
              aria-label={`${index + 1} de ${count}`}
              aria-hidden={index !== current}
            >
              <figure>
                <Photo index={item.image} sizes="(min-width: 1200px) 1100px, 100vw" />
                <figcaption>
                  <span className="ix-carousel-count">{String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}</span>
                  <strong>{item.caption}</strong>
                  {item.text ? <span className="ix-carousel-text">{item.text}</span> : null}
                </figcaption>
              </figure>
            </li>)}
          </ul>
          {count > 1 ? <>
            <button type="button" className="ix-carousel-arrow ix-carousel-arrow--prev" onClick={() => goTo(current - 1)} aria-label="Imagen anterior">
              <ChevronLeft size={22} strokeWidth={1.75} aria-hidden="true" focusable="false" />
            </button>
            <button type="button" className="ix-carousel-arrow ix-carousel-arrow--next" onClick={() => goTo(current + 1)} aria-label="Imagen siguiente">
              <ChevronRight size={22} strokeWidth={1.75} aria-hidden="true" focusable="false" />
            </button>
            <div className="ix-carousel-dots">
              {section.items.map((item, index) => <button
                key={`${item.image}-${item.caption}`}
                type="button"
                aria-label={`Ver imagen ${index + 1}: ${item.caption}`}
                aria-current={index === current ? "true" : undefined}
                onClick={() => goTo(index)}
              />)}
            </div>
          </> : null}
        </div>
      </div>
    </section>
  );
}
